class VariantWidthHeight extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.section = this.closest('.shopify-section');
    const config = this.section?.querySelector('[data-fp-cbb-config]');
    if (!config || !window.FPCbb) return;
    this.initialized = true;
    this.config = JSON.parse(config.textContent);
    this.priceRoot = this.section.querySelector('[id="price-' + this.config.sectionId + '"]');
    this.source = new FPCbb.Source(this.config);
    this.productsPromise = null;
    this.optionsReady = false;
    this.width = this.querySelector('#select-width');
    this.height = this.querySelector('#select-height');
    if (!this.prepareOptions && window.FPCbbPrepareOptions) this.prepareOptions = bounds => window.FPCbbPrepareOptions(this, bounds);
    this.prepareOptions?.();
    this.form = this.section.querySelector('product-form form.form-product');
    this.input = this.form?.querySelector('[name="id"]');
    this.button = this.form?.querySelector('[type="submit"]');
    this.quantity = this.section.querySelector('[name="quantity"]');
    this.generation = 0;
    this.listeners = new AbortController();
    this.section.addEventListener('change', (event) => {
      if (event.target === this.width || event.target === this.height) this.select();
      else if (event.target === this.quantity || event.target.matches('[name="lado-corda"]')) this.updateQuantity();
    }, { signal: this.listeners.signal });
    this.retry = this.querySelector('[data-fp-cbb-retry]');
    this.retry?.addEventListener('click', () => this.select(), { signal: this.listeners.signal });
    this.addEventListener('focusout', () => {
      if (this.pendingBounds) {
        const bounds = this.pendingBounds; this.pendingBounds = null;
        queueMicrotask(() => { if (this.isConnected && !this.contains(document.activeElement)) this.prepareOptions?.(bounds); });
      }
    }, { signal: this.listeners.signal });
    const radios = this.section.querySelectorAll('[name="lado-corda"]');
    const initialRope = this.section.querySelector('[name="lado-corda"]:checked') || radios[1] || radios[0];
    if (initialRope) initialRope.checked = true;
    window.FPRopeSelector?.sync(this.section);
    const connectedSelection = this.fpConnectedSelection;
    delete this.fpConnectedSelection;
    if (connectedSelection) this.connectedRestorePromise = this.restoreConnectedSelection(connectedSelection);
    else this.select(true);
  }

  disconnectedCallback() {
    this.generation++;
    this.confirmationController?.abort();
    this.listeners?.abort();
    this.source?.dispose();
    this.pendingBounds = null;
    this.connectedBounds = null;
    this.connectedRestoreController?.abort();
    this.initialized = false;
  }

  suspendConnectedTransition() {
    ++this.generation;
    this.confirmationController?.abort();
    this.connectedRestoreController?.abort();
    this.setState('loading');
  }

  setState(state, record = null, message = '') {
    this.state = state; this.record = record;
    this.section.fpCbbState = { state, record, sectionId: this.config.sectionId };
    const purchasable = state === 'ready' && record?.available && this.validQuantity(record);
    const hasOptions = Boolean(this.section?.querySelector('[data-fp-option-group], [data-fp-option-property]'));
    const optionsLoaded = !hasOptions || Boolean(window.FPProductOptions);
    if (hasOptions) this.form.closest('product-form').dataset.fpBaseReady = String(Boolean(purchasable));
    if (this.input) { this.input.value = record ? String(record.id) : ''; this.input.disabled = !purchasable; }
    if (this.button) {
      this.button.disabled = !purchasable || !optionsLoaded;
      this.button.toggleAttribute('data-fp-cbb-pending', state === 'loading');
      const label = this.button.querySelector('[data-atc-label]') || this.button.querySelector('span');
      if (label) label.textContent = state === 'loading' ? this.button.dataset.cbbReadyLabel : purchasable ? this.button.dataset.cbbReadyLabel : this.button.dataset.cbbUnavailableLabel;
    }
    this.form?.querySelectorAll('[data-fp-dynamic-checkout]').forEach(element => {
      element.inert = !purchasable || !optionsLoaded; element.style.visibility = purchasable && optionsLoaded ? '' : 'hidden';
    });
    const error = this.querySelector('.msg-error');
    if (error) {
      error.classList.toggle('active', Boolean(message));
      const text = error.querySelector('p'); if (text) text.textContent = message;
    }
    if (this.retry) this.retry.hidden = state !== 'error';
    this.section.querySelector('.container-summary')?.classList.toggle('active', Boolean(record));
    this.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    this.priceRoot?.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    this.priceRoot?.setAttribute('data-fp-cbb-price-pending', state === 'loading' ? 'true' : 'false');
    const status = this.querySelector('[data-fp-cbb-status]');
    if (status) status.textContent = state === 'loading' ? this.button?.dataset.cbbLoadingLabel || '' : message || (purchasable ? this.button?.dataset.cbbReadyLabel : this.button?.dataset.cbbUnavailableLabel) || '';
    window.FPProductOptions?.gate(this.section, Boolean(purchasable));
    this.section.dispatchEvent(new CustomEvent('fp:cbb:state', { detail: this.section.fpCbbState }));
  }

  async loadProducts() {
    if (!this.productsPromise) {
      const source = this.source;
      this.productsPromise = source.manifest()
        .then(products => {
          if (this.isConnected && source === this.source) this.fillOptions(products);
          return source.candidates(products);
        })
        .catch(error => { this.productsPromise = null; throw error; });
    }
    return this.productsPromise;
  }

  fillOptions(products) {
    const ranges = [[this.width, 'minWidth', 'maxWidth'], [this.height, 'minHeight', 'maxHeight']].map(([select, minKey, maxKey]) => {
      const minima = products.map(product => FPCbb.positive(product[minKey]));
      const maxima = products.map(product => FPCbb.positive(product[maxKey]));
      if (!select || minima.includes(null) || maxima.includes(null)) throw new Error('Limites de medidas não cadastrados.');
      return [select, Math.min(...minima), Math.max(...maxima)];
    });
    if (this.restoringConnected) this.connectedBounds = ranges;
    else if (ranges.some(([select]) => select === document.activeElement)) this.pendingBounds = ranges;
    else if (!this.prepareOptions?.(ranges)) throw new Error('Limites de medidas inválidos.');
    this.optionsReady = true;
  }

  async select(initial = false, preconfirmed = null) {
    const generation = ++this.generation;
    this.confirmationController?.abort();
    this.confirmationController = new AbortController();
    const signal = this.confirmationController.signal;
    this.setState('loading');
    try {
      const products = await this.loadProducts();
      if (generation !== this.generation || !this.isConnected) return;
      if (initial) {
        const id = new URL(location.href).searchParams.get('variant');
        let variant = id && String(this.config.selectedVariant?.id) === id ? this.config.selectedVariant : null;
        if (id && !variant && !products[0]?.compact) {
          variant = products.flatMap(product => product.variants).find(item => item.id === id);
        }
        if (variant) {
          for (const [select, value] of [[this.width, variant.width], [this.height, variant.height]]) {
            if (![...select.options].some(option => Number(option.value) === value)) {
              const option = document.createElement('option'); option.value = value; option.textContent = this.measureLabel(value); select.add(option);
            }
            select.value = value;
          }
        }
      }
      const requestedWidth = Number(this.width.value); const requestedHeight = Number(this.height.value);
      const winner = FPCbb.resolve(products, requestedWidth, requestedHeight);
      if (!winner) { this.setState('incompatible', null, this.dataset.cbbIncompatible); return; }
      const confirmed = preconfirmed
        && String(preconfirmed.productId) === String(winner.product.id)
        && preconfirmed.width === winner.variant.width
        && preconfirmed.height === winner.variant.height
        && (!winner.variant.id || String(preconfirmed.id) === String(winner.variant.id))
        ? preconfirmed : await this.source.confirm(winner.product, winner.variant, signal);
      if (generation !== this.generation || !this.isConnected) return;
      let technicalRows = [];
      if ([confirmed.depth, confirmed.deduction].some(value => FPCbb.number(value) === null || FPCbb.number(value) < 0)) {
        const technical = await this.source.matrix(winner.product, true);
        if (technical.variants.length !== winner.product.count ||
          !technical.variants.some(row => row.id === String(confirmed.id) && row.width === confirmed.width)) {
          throw new Error('Matriz técnica inconsistente.');
        }
        if (!winner.product.compact && technical.variants.some((row, index) =>
          row.id !== winner.product.variants[index].id || row.width !== winner.product.variants[index].width)) {
          throw new Error('Matriz técnica inconsistente.');
        }
        technicalRows = technical.variants;
      }
      if (generation !== this.generation || !this.isConnected) return;
      const record = {
        ...confirmed, id: String(confirmed.id), productId: String(confirmed.productId), requestedWidth, requestedHeight,
        depth: FPCbb.technical(confirmed, technicalRows, 'depth'),
        deduction: FPCbb.technical(confirmed, technicalRows, 'deduction'),
        quantity: Number(this.quantity?.value || 1), properties: this.properties(requestedWidth, requestedHeight),
      };
      if (this.quantity) {
        this.quantity.min = confirmed.quantityRule.min; this.quantity.step = confirmed.quantityRule.increment;
        if (confirmed.quantityRule.max === null) this.quantity.removeAttribute('max'); else this.quantity.max = confirmed.quantityRule.max;
      }
      this.commit(record);
    } catch (error) {
      if (generation !== this.generation || !this.isConnected || signal.aborted) return;
      this.setState('error', null, error?.status === 429 ? this.dataset.cbbLimited : this.dataset.cbbError);
    }
  }

  // Shared entry point for future connected color, motor and finish controls.
  // The CBB source remains the only authority for the final variant and price.
  setConnectedMeasurement(select, value) {
    if (!select || !Number.isInteger(value) || value <= 0) return;
    if (![...select.options].some(option => Number(option.value) === value)) {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = this.measureLabel(value);
      select.add(option);
    }
    select.value = String(value);
  }

  // Prime the visible controls synchronously. The new PDP is not painted until
  // connectedCallback returns, so a valid previous measure never flashes 40x40.
  async restoreConnectedSelection(saved = {}) {
    const generation = ++this.generation;
    this.confirmationController?.abort();
    this.connectedRestoreController?.abort();
    this.connectedRestoreController = new AbortController();
    const signal = this.connectedRestoreController.signal;
    this.restoringConnected = true;
    const requestedWidth = Number(saved.width);
    const requestedHeight = Number(saved.height);
    this.setConnectedMeasurement(this.width, requestedWidth);
    this.setConnectedMeasurement(this.height, requestedHeight);
    const side = [...this.section.querySelectorAll('[name="lado-corda"]')].find(input => input.value === saved.cordinha);
    if (side) side.checked = true;
    window.FPRopeSelector?.sync(this.section);
    if (this.quantity && Number.isInteger(Number(saved.quantity)) && Number(saved.quantity) > 0) {
      this.quantity.value = String(saved.quantity);
    }
    this.setState('loading');

    try {
      const products = await this.loadProducts();
      if (!this.isConnected || generation !== this.generation) return false;
      const validRequested = Number.isInteger(requestedWidth) && Number.isInteger(requestedHeight)
        && requestedWidth > 0 && requestedHeight > 0;
      const preferred = validRequested ? FPCbb.resolve(products, requestedWidth, requestedHeight) : null;
      const minimumPairs = products.flatMap(product => product.variants.map(variant => ({ width: variant.width, height: variant.height })))
        .sort((a, b) => a.width * a.height - b.width * b.height || a.width - b.width || a.height - b.height);
      const candidates = preferred ? [{ width: requestedWidth, height: requestedHeight }, ...minimumPairs] : minimumPairs;
      const seen = new Set();
      let chosen = null;
      for (const candidate of candidates) {
        const key = `${candidate.width}:${candidate.height}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const winner = FPCbb.resolve(products, candidate.width, candidate.height);
        if (!winner) continue;
        const confirmed = await this.source.confirm(winner.product, winner.variant, signal);
        if (!this.isConnected || generation !== this.generation || signal.aborted) return false;
        if (confirmed.available) { chosen = { candidate, confirmed }; break; }
      }
      if (!chosen) return false;
      if (this.connectedBounds && !this.prepareOptions?.(this.connectedBounds)) throw new Error('Limites de medidas inválidos.');
      this.connectedBounds = null;
      this.restoringConnected = false;
      this.setConnectedMeasurement(this.width, chosen.candidate.width);
      this.setConnectedMeasurement(this.height, chosen.candidate.height);
      await this.select(false, chosen.confirmed);
      if (!this.isConnected || this.state !== 'ready' || !this.record?.available) return false;
      const rule = this.record.quantityRule;
      const quantity = Number(saved.quantity);
      if (this.quantity) {
        this.quantity.value = Number.isInteger(quantity) && quantity >= rule.min
          && (rule.max === null || quantity <= rule.max) && (quantity - rule.min) % rule.increment === 0
          ? String(quantity) : String(rule.min);
        this.updateQuantity();
      }
      return true;
    } catch (error) {
      if (this.isConnected && generation === this.generation && !signal.aborted) {
        this.setState('error', null, error?.status === 429 ? this.dataset.cbbLimited : this.dataset.cbbError);
      }
      return false;
    } finally {
      this.restoringConnected = false;
      this.connectedBounds = null;
    }
  }

  properties(width, height) {
    const props = { Largura: String(width), Altura: String(height) };
    const rope = this.section.querySelector('[name="lado-corda"]:checked');
    if (rope && this.form?.querySelector('[name="properties[Lado da cordinha]"]')) props['Lado da cordinha'] = rope.value;
    return props;
  }

  validQuantity(record) {
    const { quantity: q, quantityRule: rule } = record;
    return Number.isInteger(q) && q >= rule.min && (rule.max === null || q <= rule.max) && (q - rule.min) % rule.increment === 0;
  }

  updateQuantity() {
    if (!this.record) return;
    this.commit({ ...this.record, quantity: Number(this.quantity?.value || 1), properties: this.properties(this.record.requestedWidth, this.record.requestedHeight) });
  }

  commit(record) {
    Object.freeze(record.properties); Object.freeze(record.quantityRule); Object.freeze(record);
    for (const [name, value] of Object.entries(record.properties)) {
      const input = [...(this.form?.elements || [])].find(element => element.name === 'properties[' + name + ']');
      if (input) { input.value = value; input.disabled = false; }
    }
    this.renderDetails(record);
    this.setState('ready', record, !record.available ? this.dataset.cbbUnavailable : !this.validQuantity(record) ? this.dataset.cbbQuantity : '');
  }

  text(selector, value) { this.section.querySelectorAll(selector).forEach(element => { element.textContent = value ?? ''; }); }
  priceText(selector, value) { this.priceRoot?.querySelectorAll(selector).forEach(element => { element.textContent = value ?? ''; }); }
  meters(value) { return (value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' m'; }
  measureLabel(value) { return `${this.meters(value)} (${this.config.measure === 'm_cm' ? `${value / 10} cm` : `${value} mm`})`; }
  money(cents) { return window.FPMoney.format(cents, this.config.moneyFormat); }

  renderDetails(record) {
    this.text('.select-width__summary', this.meters(record.requestedWidth));
    this.text('.select-height__summary', this.meters(record.requestedHeight));
    this.text('.dif-width-sup__summary', record.deduction !== null && record.requestedWidth > record.deduction ? this.meters(record.requestedWidth - record.deduction) : '');
    this.text('.depth__summary, .fp-product-specifications__depth-value', record.depth === null ? '' : this.meters(record.depth));
    this.text('.shipping-time__summary', record.shippingTime);
    const charged = FPCbb.positive(record.minArea) && record.requestedWidth * record.requestedHeight / 1000000 < record.minArea;
    this.section.querySelector('.container-charged')?.classList.toggle('active', Boolean(charged));
    this.text('.area-min-charged__summary', charged ? record.minAreaText : '');
    this.priceText('.price-item--sale', this.money(record.price));
    this.priceText('.price__regular .price-item--regular:not(.variant-item__old-price)', this.money(record.price));
    const discount = record.compare_at_price > record.price;
    this.priceRoot?.querySelector('.price')?.classList.toggle('price--on-sale', discount);
    this.priceRoot?.querySelector('.price')?.classList.toggle('price--sold-out', !record.available);
    const discountBox = this.priceRoot?.querySelector('.price__percentage-discount');
    if (discountBox) discountBox.hidden = !discount;
    this.priceText('.price__percentage-discount .price-item--regular', discount ? this.money(record.compare_at_price) : '');
    this.priceText('.percentage-discount', discount ? '-' + Math.round((record.compare_at_price - record.price) / record.compare_at_price * 100) + '%' : '');
    this.priceText('.quantity-installment', '12x');
    this.priceText('.price-installment', this.money(Number((record.price * 1.11 / 12).toFixed(2))));
    const measure = record.howMeasure?.children;
    this.text('.how-measure__first_line__summary', measure?.[0]?.children?.[0]?.value);
    this.text('.how-measure__second_line__summary', measure?.[1]?.children?.[0]?.children?.[0]?.value);
    this.link('.how-measure__link__summary', measure?.[1]?.children?.[0]?.children?.[1]);
    const install = record.howInstall?.children;
    this.text('.how-install__first_line__summary', install?.[0]?.children?.[0]?.value);
    this.text('.how-install__second_line__summary', install?.[1]?.children?.[0]?.children?.[0]?.value);
    this.text('.how-install__tertiary_line__summary', install?.[1]?.children?.[1]?.children?.[0]?.value);
    this.link('.how-measure__link1__summary', install?.[1]?.children?.[0]?.children?.[1]);
    this.link('.how-measure__link2__summary', install?.[1]?.children?.[1]?.children?.[1]);
  }

  link(selector, node) {
    const element = this.section.querySelector(selector); if (!element) return;
    element.textContent = node?.children?.[0]?.value || '';
    if (node?.url && /^(https?:|\/)/.test(node.url)) element.setAttribute('href', node.url); else element.removeAttribute('href');
  }
}
if (!customElements.get('variant-width-height')) customElements.define('variant-width-height', VariantWidthHeight);
